import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface PaymentMethod {
  id: string;
  name: string;
  description: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface PaymentTransaction {
  id: string;
  amount: number;
  payment_method: string;
  payment_method_id: string | null;
  reference_number: string | null;
  transaction_date: string;
  notes: string | null;
  student_billing_id: string;
  created_at: string;
  updated_at: string;
  payment_method_details?: PaymentMethod;
}

export const usePaymentMethods = () => {
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadPaymentMethods = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { data, error } = await supabase
        .from('payment_methods')
        .select('*')
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      setPaymentMethods(data || []);
    } catch (err: any) {
      
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPaymentMethods();
  }, []);

  return { paymentMethods, loading, error, refetch: loadPaymentMethods };
};

export const usePaymentTransactions = (billingId?: string) => {
  const [transactions, setTransactions] = useState<PaymentTransaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadTransactions = async () => {
    try {
      setLoading(true);
      setError(null);
      
      let query = supabase
        .from('payment_transactions')
        .select(`
          *,
          payment_method_details:payment_methods(
            id,
            name,
            description,
            is_active,
            created_at,
            updated_at
          )
        `)
        .order('transaction_date', { ascending: false });

      if (billingId) {
        query = query.eq('student_billing_id', billingId);
      }

      const { data, error } = await query;
      if (error) throw error;
      setTransactions(data || []);
    } catch (err: any) {
      
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTransactions();
  }, [billingId]);

  return { transactions, loading, error, refetch: loadTransactions };
};

// CRUD Functions for Payment Methods
export const createPaymentMethod = async (methodData: {
  name: string;
  description?: string;
  is_active?: boolean;
}) => {
  const { data, error } = await supabase
    .from('payment_methods')
    .insert(methodData)
    .select();
  
  if (error) throw error;
  return data;
};

export const updatePaymentMethod = async (id: string, updateData: {
  name?: string;
  description?: string;
  is_active?: boolean;
}) => {
  const { data, error } = await supabase
    .from('payment_methods')
    .update({ ...updateData, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select();
  
  if (error) throw error;
  return data;
};

export const deletePaymentMethod = async (id: string) => {
  // Marcar como inactivo en lugar de eliminar
  const { error } = await supabase
    .from('payment_methods')
    .update({ is_active: false, updated_at: new Date().toISOString() })
    .eq('id', id);
  
  if (error) throw error;
};

// CRUD Functions for Payment Transactions
export const createPaymentTransaction = async (transactionData: {
  amount: number;
  payment_method: string;
  payment_method_id?: string;
  reference_number?: string;
  transaction_date: string;
  notes?: string;
  student_billing_id: string;
}) => {
  const { data, error } = await supabase
    .from('payment_transactions')
    .insert(transactionData)
    .select();
  
  if (error) throw error;
  return data;
};

export const updatePaymentTransaction = async (id: string, updateData: {
  amount?: number;
  payment_method?: string;
  payment_method_id?: string;
  reference_number?: string;
  transaction_date?: string;
  notes?: string;
}) => {
  const { data, error } = await supabase
    .from('payment_transactions')
    .update({ ...updateData, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select();
  
  if (error) throw error;
  return data;
};

export const deletePaymentTransaction = async (id: string) => {
  const { error } = await supabase
    .from('payment_transactions')
    .delete()
    .eq('id', id);
  
  if (error) throw error;
};

// Función para registrar un pago y actualizar la factura
export const registerPayment = async (
  studentBillingId: string,
  paymentData: {
    amount: number;
    payment_method_id: string;
    reference_number?: string;
    notes?: string;
  }
) => {
  try {
    // Obtener el método de pago
    const { data: paymentMethod, error: methodError } = await supabase
      .from('payment_methods')
      .select('name')
      .eq('id', paymentData.payment_method_id)
      .single();

    if (methodError) throw methodError;

    // Crear la transacción de pago
    const { data: transaction, error: transactionError } = await supabase
      .from('payment_transactions')
      .insert({
        amount: paymentData.amount,
        payment_method: paymentMethod.name,
        payment_method_id: paymentData.payment_method_id,
        reference_number: paymentData.reference_number || null,
        transaction_date: new Date().toISOString().split('T')[0],
        notes: paymentData.notes || null,
        student_billing_id: studentBillingId
      })
      .select()
      .single();

    if (transactionError) throw transactionError;

    // Actualizar el estado de la factura a 'paid'
    const { error: billingError } = await supabase
      .from('student_billing')
      .update({
        status: 'paid',
        paid_date: new Date().toISOString().split('T')[0],
        updated_at: new Date().toISOString()
      })
      .eq('id', studentBillingId);

    if (billingError) throw billingError;

    return transaction;
  } catch (error) {
    
    throw error;
  }
};